import React, { useCallback, useMemo } from "react"
import { DefaultLayoutForReader } from "src/components/common/layouts/DefaultLayoutForReader"
import HTMLHead from "src/components/utils/HTMLHead"
import { GetServerSideProps } from "next"
import { useRouter } from "next/router"
import useSWR from "swr"

import { useToggleR18 } from "src/components/hooks/useToggleR18"
import { SearchResultBody } from "src/components/pages/tieups/searchResultBody"
import { fetchTieupSearch } from "src/models/tieup/search/fetchTieupSearch"
import { useInfinityLoad } from "src/components/hooks/useInfinityLoad"
import { WorkItem } from "../../models/WorkItem/WorkItem"
import { SearchResultHeader } from "../../components/SearchResultHeader"
import routes from "../../../routes"
import { fetchGenres } from "../../models/genre/fetchGenres"

type SearchParams = {
  query: string[]
  genres: string[]
  order: string
}

type Props = {
  searchParams: SearchParams
}

const LIMIT = 10

const Tieups: React.FC<Props> = ({ searchParams }) => {
  const router = useRouter()

  const { showR18, toggleShowR18 } = useToggleR18()

  // apiから取得したジャンル一覧
  const getGenreResponse = useSWR("/v1/genre", fetchGenres)
  if (getGenreResponse.error) {
    // eslint-disable-next-line no-console
    console.log(getGenreResponse.error)
  }
  const genres = useMemo(() => {
    return getGenreResponse.data || []
  }, [getGenreResponse.data])

  const getKey = useCallback(() => {
    const params = new URLSearchParams({
      q: searchParams.query.join(" "),
      g: searchParams.genres.join(","),
      o: searchParams.order,
      is_r18: showR18 ? "yes" : "no",
    })

    return `/v1/tieup/search?${params.toString()}`
  }, [searchParams, showR18])

  const fetcher = useCallback(
    (pageIndex: number) => {
      return fetchTieupSearch({
        query: searchParams.query,
        genres: searchParams.genres,
        order: searchParams.order,
        isR18: showR18 ? "yes" : "no",
        offset: pageIndex * LIMIT,
        limit: LIMIT,
      })
    },
    [showR18, searchParams],
  )

  const { list, loadMore, canLoadMore, isLoading } = useInfinityLoad<WorkItem>({
    fetcher,
    fetcherKey: getKey(),
    displayCount: LIMIT,
  })

  /** 並び順の変更 */
  const onChangeOrder = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const params = new URLSearchParams()

      if (searchParams.query.length > 0) {
        params.append("q", searchParams.query.join(" "))
      }
      if (searchParams.genres.length > 0) {
        params.append("g", searchParams.genres.join(","))
      }
      if (e.target.value === "date" || e.target.value === "dateasc") {
        params.append("o", e.target.value)
      }

      router.push(
        params.toString() ? `/tieups?${params.toString()}` : `/tieups`,
      )
    },
    [searchParams, router],
  )

  // 重複ロードを防ぐためロード中はロードしない
  const safeLoadMore = () => {
    if (isLoading) {
      return
    }

    loadMore()
  }

  /** 18禁コンテンツの表示 / 非表示 */
  const handleChangeR18Toggle = useCallback(() => {
    toggleShowR18()
  }, [toggleShowR18])

  /** 検索条件の表示 */
  const searchConditionText = useMemo(() => {
    let txt = ""

    if (searchParams.query.length > 0) {
      txt += `「${decodeURIComponent(searchParams.query.join(" "))}」`
    }

    if (searchParams.genres.length > 0 && genres) {
      const genre_ids = searchParams.genres.map((g) => parseInt(g, 10))
      const genreText = genres
        .filter((g) => genre_ids.indexOf(g.id) >= 0)
        .map((g) => g.name)
        .join(", ")
      txt += `「${genreText}」`
    }
    if (txt) {
      return `${txt}の検索結果`
    }

    return ""
  }, [searchParams, genres])

  return (
    <DefaultLayoutForReader>
      <HTMLHead path="tieups" />
      <main>
        <div className="l-single">
          <SearchResultHeader
            headerTitle={"作品検索結果一覧"}
            searchString={searchConditionText}
            genreSearchLink={routes.searchGenres()}
            onToggleR18={handleChangeR18Toggle}
            order={searchParams.order}
            onChangeOrder={onChangeOrder}
            showR18={!!showR18}
          />
          <SearchResultBody
            data={list}
            canLoadMore={canLoadMore}
            isLoading={isLoading}
            loadMore={safeLoadMore}
          />
        </div>
      </main>
    </DefaultLayoutForReader>
  )
}

export default Tieups

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  return {
    props: {
      searchParams: {
        query: query.q ? ([] as string[]).concat(query.q) : [],
        genres: query.g ? ([] as string[]).concat(query.g) : [],
        order: query.o ? query.o : "",
      },
    },
  }
}
